import type { AxiosInstance } from 'axios';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import type { ServiceResult } from '../types/common/ServiceResult';
import type { PaginatedList } from '../types/common/PaginatedList';
import type {
  AdminCorporateUserDetailDto,
  AdminCorporateUserFieldsPatchModel,
  AdminCorporateUserListItemDto,
  AdminRejectCorporateUserRequest,
  CorporateUserQueryFilter,
} from '../types/corporate';
import { apiClient } from '../client/client';

export class AdminCorporateUserService {
  private static readonly BASE = '/api/admin/corporate-users';

  constructor(private readonly client: AxiosInstance = apiClient) {}

  async list(
    filter?: CorporateUserQueryFilter
  ): Promise<ServiceResultT<PaginatedList<AdminCorporateUserListItemDto>>> {
    const params: Record<string, string | number> = {};
    if (filter?.approvalStatus) params.approvalStatus = filter.approvalStatus;
    if (filter?.search) params.search = filter.search;
    if (filter?.pageNumber != null) params.pageNumber = filter.pageNumber;
    if (filter?.pageSize != null) params.pageSize = filter.pageSize;

    const { data } = await this.client.get<
      ServiceResultT<PaginatedList<AdminCorporateUserListItemDto>>
    >(AdminCorporateUserService.BASE, { params });
    return data;
  }

  async getById(id: string): Promise<ServiceResultT<AdminCorporateUserDetailDto>> {
    const { data } = await this.client.get<ServiceResultT<AdminCorporateUserDetailDto>>(
      `${AdminCorporateUserService.BASE}/${id}`
    );
    return data;
  }

  async patchAdminFields(
    id: string,
    body: AdminCorporateUserFieldsPatchModel
  ): Promise<ServiceResult> {
    const { data } = await this.client.patch<ServiceResult>(
      `${AdminCorporateUserService.BASE}/${id}/admin-fields`,
      body
    );
    return data;
  }

  async approve(id: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${AdminCorporateUserService.BASE}/${id}/approve`
    );
    return data;
  }

  async reject(id: string, body: AdminRejectCorporateUserRequest): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${AdminCorporateUserService.BASE}/${id}/reject`,
      body
    );
    return data;
  }

  async suspend(id: string): Promise<ServiceResult> {
    const { data } = await this.client.post<ServiceResult>(
      `${AdminCorporateUserService.BASE}/${id}/suspend`
    );
    return data;
  }

  async deleteRejected(id: string): Promise<ServiceResult> {
    const { data } = await this.client.delete<ServiceResult>(
      `${AdminCorporateUserService.BASE}/${id}`
    );
    return data;
  }
}

export const adminCorporateUserService = new AdminCorporateUserService();
